import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  createRootRoute,
  Outlet,
  useRouter,
  useRouterState,
} from '@tanstack/react-router';
import { RouterProvider as AriaRouterProvider } from 'react-aria-components';
import RootLayout from 'src/components/layout/RootLayout';
import NotFoundContent from 'src/components/layout/NotFoundContent';
import useSkeletonCleanup from 'src/init/useSkeletonCleanup';
import { getDocumentTitle, getPageName } from 'src/utils/pageTitle';

const NotFound = () => {
  useSkeletonCleanup(false);

  return (
    <RootLayout>
      <NotFoundContent />
    </RootLayout>
  );
};

const RouteAnnouncer = () => {
  const [announcement, setAnnouncement] = useState('');
  const lastAnnouncementRef = useRef('');
  const { pathname, status } = useRouterState({
    select: (state) => ({
      pathname: state.location.pathname,
      status: state.status,
    }),
  });

  useEffect(() => {
    if (status !== 'idle') return undefined;

    let timeoutId: number | undefined;

    const updatePageMetadata = () => {
      const pageName = getPageName(document, pathname);
      const nextAnnouncement = `${pathname}:${pageName}`;

      document.title = getDocumentTitle(pageName);

      if (lastAnnouncementRef.current === nextAnnouncement) return;

      lastAnnouncementRef.current = nextAnnouncement;
      setAnnouncement('');

      timeoutId = window.setTimeout(() => {
        setAnnouncement(pageName);
      }, 0);
    };

    const frameId = window.requestAnimationFrame(updatePageMetadata);

    return () => {
      window.cancelAnimationFrame(frameId);

      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [pathname, status]);

  return (
    <div className="sr-only" aria-live="polite" aria-atomic="true">
      {announcement}
    </div>
  );
};

const AriaRouterContainer = ({ children }: { children: ReactNode }) => {
  const { navigate } = useRouter();

  return (
    <AriaRouterProvider navigate={(path) => navigate({ to: path })}>
      <RouteAnnouncer />
      {children}
    </AriaRouterProvider>
  );
};

export const Route = createRootRoute({
  notFoundComponent: NotFound,
  component: () => (
    <AriaRouterContainer>
      <Outlet />
    </AriaRouterContainer>
  ),
});
